import { z } from 'zod';
import { AUTOMATION_STEP_TYPES, AUTOMATION_TRIGGER_TYPES, AUTOMATION_ENQUEUE_WHITELIST, } from '../constants/automationJobs.js';
import { AUTOMATION_HUB_EVENT_TYPES } from '../constants/webhookJobs.js';
import { LEAD_STATUSES } from '../constants/leadStatus.js';
import { AUTOMATION_TEMPLATE_KEYS } from '../automation/templates.js';
const filterClauseSchema = z.object({
    path: z.string().min(1),
    op: z.enum(['eq', 'neq', 'in', 'exists', 'not_exists', 'matches', 'contains']),
    value: z.unknown().optional(),
});
export const automationFilterSchema = z.object({
    all: z.array(filterClauseSchema).optional(),
    any: z.array(filterClauseSchema).optional(),
});
const eventTriggerConfigSchema = z.object({
    event_type: z.enum(AUTOMATION_HUB_EVENT_TYPES),
});
const cronTriggerConfigSchema = z.object({
    cron: z.string().min(9).max(64),
    timezone: z.string().min(1).max(64).default('Europe/Amsterdam'),
});
const manualTriggerConfigSchema = z.object({}).optional();
export const automationStepSchema = z.object({
    step_order: z.number().int().min(0),
    step_type: z.enum(AUTOMATION_STEP_TYPES),
    config: z.record(z.string(), z.unknown()).default({}),
    enabled: z.boolean().optional(),
});
export const automationCreateSchema = z
    .object({
    project_id: z.string().uuid(),
    name: z.string().min(1).max(120),
    description: z.string().max(2000).optional(),
    enabled: z.boolean().optional(),
    trigger_type: z.enum(AUTOMATION_TRIGGER_TYPES),
    trigger_config: z.record(z.string(), z.unknown()).default({}),
    filter: automationFilterSchema.optional(),
    steps: z.array(automationStepSchema).min(1).max(20),
})
    .superRefine((body, ctx) => {
    if (body.trigger_type === 'event') {
        const parsed = eventTriggerConfigSchema.safeParse(body.trigger_config);
        if (!parsed.success) {
            ctx.addIssue({
                code: 'custom',
                message: 'trigger_config.event_type required for event trigger',
                path: ['trigger_config'],
            });
        }
    }
    if (body.trigger_type === 'cron') {
        const parsed = cronTriggerConfigSchema.safeParse(body.trigger_config);
        if (!parsed.success) {
            ctx.addIssue({ code: 'custom', message: 'Invalid cron trigger_config', path: ['trigger_config'] });
        }
    }
    if (body.trigger_type === 'manual') {
        manualTriggerConfigSchema.parse(body.trigger_config ?? {});
    }
    for (const step of body.steps) {
        if (step.step_type === 'lead_set_status') {
            const status = step.config.status;
            if (typeof status !== 'string' || !LEAD_STATUSES.includes(status)) {
                ctx.addIssue({ code: 'custom', message: 'Invalid lead status in step', path: ['steps'] });
            }
        }
        if (step.step_type === 'enqueue_job') {
            const job = step.config.job_name;
            if (typeof job !== 'string' ||
                !AUTOMATION_ENQUEUE_WHITELIST.includes(job)) {
                ctx.addIssue({ code: 'custom', message: 'job_name not whitelisted', path: ['steps'] });
            }
        }
        if (step.step_type === 'run_agent') {
            const agentId = step.config.agent_id;
            if (typeof agentId !== 'string' || !/^[0-9a-f-]{36}$/i.test(agentId)) {
                ctx.addIssue({ code: 'custom', message: 'run_agent requires agent_id uuid', path: ['steps'] });
            }
        }
    }
});
export const automationUpdateSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(2000).nullable().optional(),
    enabled: z.boolean().optional(),
    trigger_config: z.record(z.string(), z.unknown()).optional(),
    filter: automationFilterSchema.optional(),
    steps: z.array(automationStepSchema).min(1).max(20).optional(),
});
export const automationsListQuerySchema = z.object({
    project_id: z.string().uuid(),
});
export const automationReportsListQuerySchema = z.object({
    project_id: z.string().uuid(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
});
export const automationManualRunSchema = z.object({
    payload: z.record(z.string(), z.unknown()).optional(),
    idempotency_key: z.string().min(1).max(128).optional(),
});
export const automationSimulateSchema = z.object({
    project_id: z.string().uuid(),
    event_type: z.string().min(1).optional(),
    payload: z.record(z.string(), z.unknown()).default({}),
    filter: automationFilterSchema.optional(),
    steps: z.array(automationStepSchema).min(1).max(20),
});
export const automationFromTemplateSchema = z.object({
    project_id: z.string().uuid(),
    template_key: z.enum(AUTOMATION_TEMPLATE_KEYS),
    params: z.record(z.string(), z.string()).default({}),
    name: z.string().min(1).max(120).optional(),
    enabled: z.boolean().optional(),
});
//# sourceMappingURL=automation.js.map